import { Suspense, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

type DeferredMegaMenuProps = {
  label: string;
  fallbackHref: string;
  load: () => Promise<unknown>;
  children: (initialOpen: boolean) => ReactNode;
};

export function DeferredMegaMenu({ label, fallbackHref, load, children }: DeferredMegaMenuProps) {
  const [ready, setReady] = useState(false);
  const [requestedOpen, setRequestedOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const pendingLoad = useRef<Promise<unknown> | null>(null);

  const preload = () => {
    if (ready || failed) {
      return;
    }

    pendingLoad.current ??= load()
      .then(() => setReady(true))
      .catch(() => setFailed(true));
  };

  const requestOpen = () => {
    setRequestedOpen(true);
    preload();
  };

  const trigger = (
    <button
      type="button"
      className="flex items-center gap-1.5 text-foreground/80 hover:text-secondary font-medium text-sm transition-colors"
      onClick={requestOpen}
      aria-busy="true"
      aria-haspopup="true"
    >
      {label}
      <ChevronDown className="w-4 h-4" />
    </button>
  );

  if (failed) {
    return (
      <Link to={fallbackHref} className="text-foreground/80 hover:text-secondary font-medium text-sm transition-colors">
        {label}
      </Link>
    );
  }

  if (ready) {
    return <Suspense fallback={trigger}>{children(requestedOpen)}</Suspense>;
  }

  return (
    <div
      className="relative"
      onMouseEnter={requestOpen}
      onMouseLeave={() => setRequestedOpen(false)}
      onFocus={requestOpen}
    >
      {trigger}
    </div>
  );
}
